/* import __COLOCATED_TEMPLATE__ from './sprint-bracket-fis50-page.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';

export default class SprintBracketFis50Page extends Component
{
	get quarterfinalsLLs()
	{
		return this.calculateLLFromQuarters(this.args.quarterfinalsResults);
	}

	get quarterfinalsResultsForDisplay()
	{
		// Ensure LLs are calculated first so isLL flags are set
		this.quarterfinalsLLs;
		return this.args.quarterfinalsResults;
	}

	get semifinalsLLs()
	{
		return this.calculateLLFromSemis(this.args.semifinalsResults);
	}

	get semifinalsResultsWithLLs()
	{
		// Ensure LLs are calculated first so isLL flags are set
		this.semifinalsLLs;
		return this.args.semifinalsResults;
	}

	get semifinalsResultsForDisplay()
	{
		let results = this.args.semifinalsResults;
		if(!this.args.semifinalsClassGroups) return results || [];

		let S1Results = this.filterResultsForClassGroup(results,this.args.semifinalsClassGroups[0]);
		let S2Results = this.filterResultsForClassGroup(results,this.args.semifinalsClassGroups[1]);

		//populate display results with start list live from QF results until a real
		//start list has been created, then just return the real results
		if(this.args.showQuarterfinals && (S1Results.length == 0 && S2Results.length == 0))
		{
			return this.calculateSemisAssignmentFromQuarters();
		}
		else
		{
			return this.semifinalsResultsWithLLs;
		}
	}

	get finalsResultsForDisplay()
	{
		let results = this.args.finalsResults;
		if(!this.args.finalsClassGroups) return results || [];

		let AResults = this.filterResultsForClassGroup(results,this.args.finalsClassGroups[0]);

		//populate display results with start list live from SF results until a real
		//start list has been created, then just return the real results
		if(AResults.length == 0)
		{
			return this.calculateFinalsAssignmentFromSemis();
		}
		else
		{
			return results;
		}
	}

	filterResultsForClassGroup(results,classGroupId)
	{
		if(results == null)
			return [];

		return results.filter(function(result)
		{
			return result.ClassGroupId == classGroupId;
		});
	}

	finishedOnlyFilter(result)
	{
		return result.totalTimeInMilliseconds != undefined;
	}

	sortFn(a,b)
	{
		//records with time above those without
		let aTime = a.totalTimeInMilliseconds;
		let bTime = b.totalTimeInMilliseconds;

		let aStatus = a.status;
		let bStatus = b.status;

		//always rank SUS status at bottom
		if(aStatus == "SUS" || aStatus == "RAL")
			return 1;
		if(bStatus == "SUS" || bStatus == "RAL")
			return -1

		if(aTime != null && bTime != null)
			return aTime>bTime ? 1 : aTime<bTime ? -1 : (a.id>b.id?1:-1);
		if(aTime != null)
			return -1;
		if(bTime != null)
			return 1;
		return (a.id>b.id?1:-1);
	}

	calculateLLFromQuarters(results)
	{
		//get results for each classgroup
		let Q1Results = this.filterResultsForClassGroup(results,this.args.quarterfinalsClassGroups[0]);
		let Q2Results = this.filterResultsForClassGroup(results,this.args.quarterfinalsClassGroups[1]);
		let Q3Results = this.filterResultsForClassGroup(results,this.args.quarterfinalsClassGroups[2]);
		let Q4Results = this.filterResultsForClassGroup(results,this.args.quarterfinalsClassGroups[3]);
		let Q5Results = this.filterResultsForClassGroup(results,this.args.quarterfinalsClassGroups[4]);

		let Q1ResultsFiltered = Q1Results.filter(this.finishedOnlyFilter);
		let Q2ResultsFiltered = Q2Results.filter(this.finishedOnlyFilter);
		let Q3ResultsFiltered = Q3Results.filter(this.finishedOnlyFilter);
		let Q4ResultsFiltered = Q4Results.filter(this.finishedOnlyFilter);
		let Q5ResultsFiltered = Q5Results.filter(this.finishedOnlyFilter);

		Q1ResultsFiltered.sort(this.sortFn);
		Q2ResultsFiltered.sort(this.sortFn);
		Q3ResultsFiltered.sort(this.sortFn);
		Q4ResultsFiltered.sort(this.sortFn);
		Q5ResultsFiltered.sort(this.sortFn);

		// Collect everyone from 4th place onwards (after top 3 who advance directly)
		// LLs are the next fastest 5 from any position across all QFs
		let llCandidates = [];

		const collectLLCandidates = (results) => {
			// Skip top 3 (positions 0,1,2), collect everyone else
			for (let i = 3; i < results.length; i++) {
				if (results[i] && results[i].status == 'OK' && results[i].totalTimeInMilliseconds != null) {
					llCandidates.push(results[i]);
				}
			}
		};

		collectLLCandidates(Q1ResultsFiltered);
		collectLLCandidates(Q2ResultsFiltered);
		collectLLCandidates(Q3ResultsFiltered);
		collectLLCandidates(Q4ResultsFiltered);
		collectLLCandidates(Q5ResultsFiltered);

		// Sort by time and take fastest 5
		llCandidates.sort((a, b) => a.totalTimeInMilliseconds - b.totalTimeInMilliseconds);

		const LLs = llCandidates.slice(0, 5);
		LLs.forEach(ll => ll.isLL = true);

		return LLs;
	}

	calculateLLFromSemis(results)
	{
		//get results for each classgroup
		let S1Results = this.filterResultsForClassGroup(results,this.args.semifinalsClassGroups[0]);
		let S2Results = this.filterResultsForClassGroup(results,this.args.semifinalsClassGroups[1]);

		S1Results = S1Results.filter(this.finishedOnlyFilter);
		S2Results = S2Results.filter(this.finishedOnlyFilter);

		S1Results.sort(this.sortFn);
		S2Results.sort(this.sortFn);

		// Collect 4th-7th place from each SF (positions 3,4,5,6 in 0-indexed)
		let llCandidates = [];

		const collectLLCandidates = (results) => {
			for (let i = 3; i <= 6; i++) {
				if (results[i] && results[i].status == 'OK' && results[i].totalTimeInMilliseconds != null) {
					llCandidates.push(results[i]);
				}
			}
		};

		collectLLCandidates(S1Results);
		collectLLCandidates(S2Results);

		// Sort by time and take fastest 4
		llCandidates.sort((a, b) => a.totalTimeInMilliseconds - b.totalTimeInMilliseconds);

		const LLs = llCandidates.slice(0, 4);
		LLs.forEach(ll => ll.isLL = true);

		return LLs;
	}

	calculateSemisAssignmentFromQuarters()
	{
		let S1Assignments = [];
		let S2Assignments = [];

		let quarterfinalsResults = this.args.quarterfinalsResults;

		//get results for each classgroup
		let Q1Results = this.filterResultsForClassGroup(quarterfinalsResults,this.args.quarterfinalsClassGroups[0]);
		let Q2Results = this.filterResultsForClassGroup(quarterfinalsResults,this.args.quarterfinalsClassGroups[1]);
		let Q3Results = this.filterResultsForClassGroup(quarterfinalsResults,this.args.quarterfinalsClassGroups[2]);
		let Q4Results = this.filterResultsForClassGroup(quarterfinalsResults,this.args.quarterfinalsClassGroups[3]);
		let Q5Results = this.filterResultsForClassGroup(quarterfinalsResults,this.args.quarterfinalsClassGroups[4]);

		let Q1ResultsFiltered = Q1Results.filter(this.finishedOnlyFilter);
		let Q2ResultsFiltered = Q2Results.filter(this.finishedOnlyFilter);
		let Q3ResultsFiltered = Q3Results.filter(this.finishedOnlyFilter);
		let Q4ResultsFiltered = Q4Results.filter(this.finishedOnlyFilter);
		let Q5ResultsFiltered = Q5Results.filter(this.finishedOnlyFilter);

		Q1ResultsFiltered.sort(this.sortFn);
		Q2ResultsFiltered.sort(this.sortFn);
		Q3ResultsFiltered.sort(this.sortFn);
		Q4ResultsFiltered.sort(this.sortFn);
		Q5ResultsFiltered.sort(this.sortFn);

		// S1: Q1.1, Q1.2, Q2.1, Q2.2, Q3.2, Q1.3, Q2.3, LL3, LL4, LL5
		// S2: Q3.1, Q4.1, Q5.1, Q4.2, Q5.2, Q3.3, Q4.3, Q5.3, LL1, LL2

		// Q1: all 3 to S1
		if(Q1ResultsFiltered.length > 0)
			S1Assignments.push(Object.assign({},Q1ResultsFiltered[0]));
		if(Q1ResultsFiltered.length > 1)
			S1Assignments.push(Object.assign({},Q1ResultsFiltered[1]));
		if(Q1ResultsFiltered.length > 2)
			S1Assignments.push(Object.assign({},Q1ResultsFiltered[2]));

		// Q2: all 3 to S1
		if(Q2ResultsFiltered.length > 0)
			S1Assignments.push(Object.assign({},Q2ResultsFiltered[0]));
		if(Q2ResultsFiltered.length > 1)
			S1Assignments.push(Object.assign({},Q2ResultsFiltered[1]));
		if(Q2ResultsFiltered.length > 2)
			S1Assignments.push(Object.assign({},Q2ResultsFiltered[2]));

		// Q3: 1st to S2, 2nd to S1, 3rd to S2
		if(Q3ResultsFiltered.length > 0)
			S2Assignments.push(Object.assign({},Q3ResultsFiltered[0]));
		if(Q3ResultsFiltered.length > 1)
			S1Assignments.push(Object.assign({},Q3ResultsFiltered[1]));
		if(Q3ResultsFiltered.length > 2)
			S2Assignments.push(Object.assign({},Q3ResultsFiltered[2]));

		// Q4: all 3 to S2
		if(Q4ResultsFiltered.length > 0)
			S2Assignments.push(Object.assign({},Q4ResultsFiltered[0]));
		if(Q4ResultsFiltered.length > 1)
			S2Assignments.push(Object.assign({},Q4ResultsFiltered[1]));
		if(Q4ResultsFiltered.length > 2)
			S2Assignments.push(Object.assign({},Q4ResultsFiltered[2]));

		// Q5: all 3 to S2
		if(Q5ResultsFiltered.length > 0)
			S2Assignments.push(Object.assign({},Q5ResultsFiltered[0]));
		if(Q5ResultsFiltered.length > 1)
			S2Assignments.push(Object.assign({},Q5ResultsFiltered[1]));
		if(Q5ResultsFiltered.length > 2)
			S2Assignments.push(Object.assign({},Q5ResultsFiltered[2]));

		// Lucky losers: LL1, LL2 to S2; LL3, LL4, LL5 to S1
		const LLs = this.quarterfinalsLLs;
		if(LLs.length > 0)
			S2Assignments.push(Object.assign({},LLs[0]));
		if(LLs.length > 1)
			S2Assignments.push(Object.assign({},LLs[1]));
		if(LLs.length > 2)
			S1Assignments.push(Object.assign({},LLs[2]));
		if(LLs.length > 3)
			S1Assignments.push(Object.assign({},LLs[3]));
		if(LLs.length > 4)
			S1Assignments.push(Object.assign({},LLs[4]));

		let tempIds=0;
		for(let assignment of S1Assignments)
		{
			assignment.ClassGroupId=this.args.semifinalsClassGroups[0];
			assignment.place = null;
			assignment.totalTimeInMilliseconds = null;
			assignment.isLL = false;
			assignment.id=tempIds;
			tempIds++;
		}
		for(let assignment of S2Assignments)
		{
			assignment.ClassGroupId=this.args.semifinalsClassGroups[1];
			assignment.place = null;
			assignment.totalTimeInMilliseconds = null;
			assignment.isLL = false;
			assignment.id=tempIds;
			tempIds++;
		}

		return S1Assignments.concat(S2Assignments);
	}

	calculateFinalsAssignmentFromSemis()
	{
		let AAssignments = [];

		let semifinalsResults = this.args.semifinalsResults;

		//get results for each classgroup
		let S1Results = this.filterResultsForClassGroup(semifinalsResults,this.args.semifinalsClassGroups[0]);
		let S2Results = this.filterResultsForClassGroup(semifinalsResults,this.args.semifinalsClassGroups[1]);

		let S1ResultsFiltered = S1Results.filter(this.finishedOnlyFilter);
		let S2ResultsFiltered = S2Results.filter(this.finishedOnlyFilter);

		S1ResultsFiltered.sort(this.sortFn);
		S2ResultsFiltered.sort(this.sortFn);

		// Top 3 from each semi (6 total)
		if(S1ResultsFiltered.length > 0)
			AAssignments.push(Object.assign({},S1ResultsFiltered[0]));
		if(S2ResultsFiltered.length > 0)
			AAssignments.push(Object.assign({},S2ResultsFiltered[0]));
		if(S1ResultsFiltered.length > 1)
			AAssignments.push(Object.assign({},S1ResultsFiltered[1]));
		if(S2ResultsFiltered.length > 1)
			AAssignments.push(Object.assign({},S2ResultsFiltered[1]));
		if(S1ResultsFiltered.length > 2)
			AAssignments.push(Object.assign({},S1ResultsFiltered[2]));
		if(S2ResultsFiltered.length > 2)
			AAssignments.push(Object.assign({},S2ResultsFiltered[2]));

		// Plus 4 LLs = 10 total for A Final
		let LLs = this.semifinalsLLs;
		for(let ll of LLs) {
			AAssignments.push(Object.assign({},ll));
		}

		let tempIds=0;
		for(let assignment of AAssignments)
		{
			assignment.ClassGroupId=this.args.finalsClassGroups[0];
			assignment.place = null;
			assignment.totalTimeInMilliseconds = null;
			assignment.isLL = false;
			assignment.id=tempIds;
			tempIds++;
		}
		return AAssignments;
	}
}
